import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ExportTypeEnum } from "@models/common/export-type.enum";
import { EmailRecord } from "@models/email-templates/email-record";
import { EmailStatus } from "@models/outgoing-messages/email-status.enum";
import { OutgoingEmailsExportService } from "@pages/outgoing-messages/services/outgoing-emails-export.service";
import { OutgoingEmailsGridService } from "@pages/outgoing-messages/services/outgoing-emails-grid.service";
import { CommonPageComponent } from "@shared/components/commons/common-page/common-page.component";
import {
  GridServerSideComponent
} from "@shared/components/grid/components/grid-server-side/grid-server-side.component";
import { ButtonComponent } from "@shared/controls/button/button.component";
import { HasRoleDirective } from "@shared/directives/has-role.directive";
import { EmailsApiService } from "@shared/services/emails-api.service";
import { IPartialHeaderData } from "@shared/services/header.service";
import { MenuService } from "@shared/services/menu.service";
import { OrganisationApiService } from "@shared/services/organisation-api.service";
import { PageSizeService } from "@shared/services/page-size.service";
import { excelReportTitleStyles } from "@shared/utils/excel-title-for-report";
import {
  CellClickedEvent,
  ExcelExportParams,
  ExcelStyle,
  GetRowIdParams,
  GridApi,
  IServerSideGetRowsRequest
} from "ag-grid-community";
import { GridOptions } from "ag-grid-enterprise";
import { ToastrService } from "ngx-toastr";
import { ModalFormsService } from "../../../../forms/services/modal-forms.service";
import {
  AgOutgoingMessagesActionsComponent
} from "./components/ag-ougoing-emails-actions/ag-outgoing-messages-actions.component";
import {
  OutgoingEmailMessageDetailsComponent
} from "./components/outgoing-email-message-details/outgoing-email-message-details.component";

@Component({
  selector: 'app-outgoing-email-messages',
  standalone: true,
  imports: [
    ButtonComponent,
    OutgoingEmailMessageDetailsComponent,
    HasRoleDirective,
    GridServerSideComponent
  ],
  providers: [
    EmailsApiService,
    OutgoingEmailsGridService,
    OutgoingEmailsExportService,
  ],
  templateUrl: 'outgoing-email-messages-page.component.html',
  styleUrl: 'outgoing-email-messages-page.component.scss',
})
export class OutgoingEmailMessagesPageComponent extends CommonPageComponent implements OnInit {
  private modalFormsService = inject(ModalFormsService);
  private emailTemplatesService = inject(EmailsApiService);
  private outgoingEmailsGridExportService = inject(OutgoingEmailsGridService);
  private outgoingEmailsExportService = inject(OutgoingEmailsExportService);
  private organisationService = inject(OrganisationApiService);
  private toastr = inject(ToastrService);
  private menuService = inject(MenuService);
  private destroyRef = inject(DestroyRef);

  override readonly headerData: IPartialHeaderData = {
    title: 'Outgoing Email Messages',
    iconClass: 'ic-outgoing-emails-20'
  };

  currentEmailRecordIndex = -1;
  currentEmailMessageItemNumber = 0;
  checkedEmailRecords: EmailRecord[] = [];
  emailRows: EmailRecord[] = [];
  isDetailsLoading = false;
  $selectedEmailRecord = signal<EmailRecord | null>(null);
  modalOpened = this.modalFormsService.modalOpened;

  override ngOnInit(): void {
    super.ngOnInit();
    this.listenGridDataLoaded();
    this.setGridConfig();
    this.menuService.setMenuItemOpenState('Outgoing Messages');
  }

  listenGridDataLoaded(): void {
    this.outgoingEmailsGridExportService.dataLoaded$.asObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.emailRows = result.records;

        if (this.currentEmailRecordIndex >= 0) {
          this.updateSelectedEmail();
        }
      });
  }

  // =====  Actions  =====

  cellClickHandler(params: CellClickedEvent<EmailRecord>) {
    this.openEmailRecordPreview({
      record: params.data as EmailRecord,
      index: params.rowIndex as number
    });
  }

  resendEmailMessage(emailRecord: EmailRecord): void {
    this.emailTemplatesService.resendOutgoingEmailMessage(emailRecord.emailMessageId)
      .subscribe({
        next: () => this.reloadGrid(),
        error: () => this.toastr.error('Something happened while sending email', 'Error')
      });
  }

  deleteEmailMessage(emailRecord: EmailRecord): void {
    if (confirm('Deleting the email will not cancel pending eSigning envelopes or recall sent emails. To cancel an eSigning request, you must void the envelope. Do you want to continue?')) {
      this.emailTemplatesService.deleteOutgoingEmailMessage(emailRecord.emailMessageId)
        .subscribe({
          next: () => this.reloadGrid(),
          error: () => this.toastr.error('Something went wrong with deleting!', 'Error'),
        });
    }
  }

  // =====  Handle email details  =====

  openEmailRecordPreview(data: { record: EmailRecord, index: number }): void {
    this.currentEmailRecordIndex = data.index;
    this.updateSelectedEmail();
  }

  previousEmailMessage(): void {
    if (this.currentEmailRecordIndex > 0) {
      const pageSize = this.outgoingEmailsGridExportService.gridApi.paginationGetPageSize();
      const currentPage = this.outgoingEmailsGridExportService.gridApi.paginationGetCurrentPage();
      const lastElementPrevPageIndex = pageSize * currentPage - 1;

      this.currentEmailRecordIndex--;
      if (this.currentEmailRecordIndex === lastElementPrevPageIndex) {
        this.outgoingEmailsGridExportService.gridApi.paginationGoToPreviousPage();
        this.isDetailsLoading = true;
      }
      this.updateSelectedEmail();
    }
  }

  nextEmailMessage(): void {
    if (this.currentEmailRecordIndex < this.totalRows()) {
      const pageSize = this.outgoingEmailsGridExportService.gridApi.paginationGetPageSize();
      const currentPage = this.outgoingEmailsGridExportService.gridApi.paginationGetCurrentPage();
      const firstElementNextPageIndex = (pageSize * currentPage + pageSize);

      this.currentEmailRecordIndex++;
      if (this.currentEmailRecordIndex === firstElementNextPageIndex) {
        this.outgoingEmailsGridExportService.gridApi.paginationGoToNextPage();
        this.isDetailsLoading = true;
      }
      this.updateSelectedEmail();
    }
  }

  updateSelectedEmail(): void {
    const pageSize = this.outgoingEmailsGridExportService.gridApi.paginationGetPageSize();
    const currentPage = this.outgoingEmailsGridExportService.gridApi.paginationGetCurrentPage();

    this.currentEmailMessageItemNumber = this.currentEmailRecordIndex + 1;

    let currentIndex = 0;
    if (this.currentEmailRecordIndex < pageSize) {
      currentIndex = this.currentEmailRecordIndex;
    } else {
      const countRows = (pageSize * currentPage);
      if (countRows) {
        currentIndex = this.currentEmailRecordIndex % (pageSize * currentPage);
      } else {
        currentIndex = 0;
      }
    }

    if (!this.isDetailsLoading)
      this.$selectedEmailRecord.set(this.emailRows[currentIndex]);
  }

  onCloseDetails(): void {
    this.currentEmailRecordIndex = -1;
    this.$selectedEmailRecord.set(null);
    this.reloadGrid();
  }

  // =====  Grid  =====

  readonly defaultPageSize = PageSizeService.defaultPageSize;
  readonly maxBlocksInCache = 1;
  readonly cacheBlockSize = 20;
  readonly gridOptions: GridOptions = {
    overlayNoRowsTemplate: '<span class="no-rows-overlay">No data available</span>',
    getRowId: (params: GetRowIdParams<EmailRecord>) => params.data.emailMessageId,
  };

  dataSource = this.outgoingEmailsGridExportService.dataSource;
  colDefs = this.outgoingEmailsGridExportService.colDefs;
  totalRows = this.outgoingEmailsGridExportService.totalRecords;

  onGridReady(gridApi: GridApi): void {
    this.outgoingEmailsGridExportService.setGridApi(gridApi);
  }

  selectEmailRecord(emailRecords: EmailRecord[]): void {
    this.checkedEmailRecords = emailRecords;
  }

  onSearch(searchText = ''): void {
    this.outgoingEmailsGridExportService.setDataSource(searchText);
  }

  setGridFilterOptions(): void {
    const profileUserEmailsList = this.organisationService.getCachedProfileUsers().map(u => u?.email);
    this.outgoingEmailsGridExportService.setGridFilterOptions(profileUserEmailsList);
  }

  setGridConfig(): void {
    this.setGridFilterOptions();

    const actionCell = {
      width: 60,
      minWidth: 60,
      sortable: false,
      cellRenderer: AgOutgoingMessagesActionsComponent,
      cellRendererParams: {
        resend: this.resendEmailMessage.bind(this),
        delete: this.deleteEmailMessage.bind(this),
        checkShowResend: (data: EmailRecord | undefined) => data?.status === EmailStatus.Failed,
        checkShowDelete: (data: EmailRecord | undefined) => data?.status !== EmailStatus.Created,
        checkDisabled: (data: EmailRecord | undefined) => data?.status === EmailStatus.Created,
      }
    };

    this.outgoingEmailsGridExportService.setColDefsWithCellClickHandler(actionCell, this.cellClickHandler.bind(this));
  }

  reloadGrid(): void {
    this.outgoingEmailsGridExportService.gridApi.refreshServerSide();
  }

  // =====  Export  =====

  readonly ExportTypeEnum = ExportTypeEnum;
  readonly excelExportParams = this.outgoingEmailsExportService.exportParams() as ExcelExportParams;
  readonly excelStyles: ExcelStyle[] = excelReportTitleStyles;

  exportSelectedEmailMessagesToXls(): void {
    this.exportEmailMessagesList(true, ExportTypeEnum.EXCEL);
  }

  exportEmailMessagesList(isBulkExport: boolean, exportType: ExportTypeEnum): void {
    if (this.modalOpened()) return;

    this.outgoingEmailsExportService.exportEmailMessagesList(
      this.outgoingEmailsGridExportService.gridApi,
      this.colDefs(),
      this.checkedEmailRecords,
      this.totalRows(),
      isBulkExport,
      exportType
    );
  }
}
